// Разметка внутри строки лога — так же, как её показывает claude code в
// консоли: жирное начертание вместо `**`, наклонное вместо `_`, код фоном
// вместо обратных кавычек. Полноценный markdown здесь не нужен и вреден:
// строка лога должна остаться одной строкой моноширинного текста, просто
// с правильными начертаниями.
//
// Порядок разбора важен: код разбирается первым и внутрь него не заглядываем,
// иначе путь `**/*.py` в команде превратился бы в жирный текст.

import type { ReactNode } from 'react'
import './table.css'

/** Код, жирное, наклонное, ссылка — одним проходом, код имеет приоритет. */
const TOKEN = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(__[^_\n]+__)|(\[[^\]\n]+\]\([^)\s]+\))/g

/** Заголовок markdown: консоль печатает его жирным, решётки не показывает. */
const HEADING = /^(#{1,6})\s+(.*)$/

function pieces(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null
  TOKEN.lastIndex = 0
  while ((m = TOKEN.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const key = `${keyPrefix}-${m.index}`
    if (m[1]) {
      out.push(<code className="t-code" key={key}>{m[1].slice(1, -1)}</code>)
    } else if (m[2]) {
      out.push(<b className="t-b" key={key}>{m[2].slice(2, -2)}</b>)
    } else if (m[3]) {
      out.push(<b className="t-b" key={key}>{m[3].slice(2, -2)}</b>)
    } else if (m[4]) {
      const cut = m[4].indexOf('](')
      const label = m[4].slice(1, cut)
      const href = m[4].slice(cut + 2, -1)
      out.push(
        <a className="t-link" href={href} target="_blank" rel="noreferrer" key={key}>
          {label}
        </a>,
      )
    }
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Ограждённый блок кода приходит одной строкой лога: ```\nтело\n``` */
const FENCE = /^```[^\n]*\n([\s\S]*?)\n?```$/

// ---------- таблицы ----------
//
// Таблица агента приходит одной строкой лога, строки разделены \n. Консоль
// рисует её рамкой из ┌─┬─┐ с выровненными колонками — здесь то же самое
// обычной таблицей с тонкими линиями: колонки выравнивает браузер, длинные
// ячейки переносятся, а слишком широкая таблица прокручивается вбок.

type Align = 'left' | 'center' | 'right' | undefined

/** Разделитель под шапкой: | --- | :---: | ---: | */
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/

function cells(row: string): string[] {
  let r = row.trim()
  if (r.startsWith('|')) r = r.slice(1)
  if (r.endsWith('|') && !r.endsWith('\\|')) r = r.slice(0, -1)
  // \| внутри ячейки — буквальная черта, а не граница
  return r.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'))
}

function aligns(sep: string): Align[] {
  return cells(sep).map((c) => {
    const l = c.startsWith(':')
    const r = c.endsWith(':')
    return l && r ? 'center' : r ? 'right' : l ? 'left' : undefined
  })
}

function table(rows: string[], keyPrefix: string): ReactNode {
  const head = cells(rows[0])
  const al = aligns(rows[1])
  const body = rows.slice(2).map(cells)
  const width = Math.max(head.length, ...body.map((r) => r.length))
  const cols = Array.from({ length: width }, (_, i) => i)
  return (
    <span className="t-table" key={keyPrefix}>
      <table>
        <thead>
          <tr>
            {cols.map((i) => (
              <th key={i} style={{ textAlign: al[i] }}>{pieces(head[i] ?? '', `${keyPrefix}-h${i}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri}>
              {cols.map((i) => (
                <td key={i} style={{ textAlign: al[i] }}>
                  {pieces(r[i] ?? '', `${keyPrefix}-${ri}-${i}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </span>
  )
}

/** Разрезать текст на куски «текст / таблица»; null — таблиц нет. */
function splitTables(text: string): { table: boolean; lines: string[] }[] | null {
  if (!text.includes('|') || !text.includes('\n')) return null
  const lines = text.split('\n')
  const out: { table: boolean; lines: string[] }[] = []
  let found = false
  let i = 0
  const push = (isTable: boolean, chunk: string[]) => {
    const last = out[out.length - 1]
    if (!isTable && last && !last.table) last.lines.push(...chunk)
    else out.push({ table: isTable, lines: chunk })
  }
  while (i < lines.length) {
    const line = lines[i]
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1])) {
      let j = i + 2
      while (j < lines.length && lines[j].includes('|') && lines[j].trim() !== '') j++
      push(true, lines.slice(i, j))
      found = true
      i = j
    } else {
      push(false, [line])
      i++
    }
  }
  return found ? out : null
}

/** Строка лога с начертаниями. Пустой текст возвращается как есть. */
export function inline(text: string, keyPrefix = 'i'): ReactNode {
  if (!text) return text
  const parts = splitTables(text)
  if (parts) {
    return parts.map((p, i) => {
      const key = `${keyPrefix}-t${i}`
      if (p.table) return table(p.lines, key)
      // текст вокруг таблицы — построчно, чтобы работали заголовки и начертания
      return (
        <span className="t-para" key={key}>
          {p.lines.map((l, j) => (
            <span key={j}>{j > 0 ? '\n' : null}{inline(l, `${key}-${j}`)}</span>
          ))}
        </span>
      )
    })
  }
  const fence = FENCE.exec(text)
  // Обратные кавычки консоль не печатает — показывает само тело блока.
  if (fence) return <span className="t-fence">{fence[1]}</span>
  const head = HEADING.exec(text)
  if (head) {
    return <b className="t-b">{pieces(head[2], keyPrefix)}</b>
  }
  if (!/[`*_[]/.test(text)) return text
  return pieces(text, keyPrefix)
}
